import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../../../../components/ui/table';
import { Button } from '../../../../../../components/ui/button';
import { MultiSelect } from '../../../../../../components/ui/multi-select';
import { Filter, X, Upload, AlertTriangle, ChevronUp, ChevronDown, ArrowLeft, Download } from 'lucide-react';
import { useCoatingPumphouse } from '../../../../../../redux/CoatingPumphouse/hooks/useCoatingPumphouse';
import SprayDashboardSection from './SprayDashboardSection';
import SessionsAggregateTable from './SessionsAggregateTable';
import SessionDrilldownSection from './SessionDrilldownSection';
import PumpHouseLimitsToolbar from './PumpHouseLimitsToolbar';
import {
  buildSessionSummaries,
  buildSessionExcursionTotals,
  computeSessionKpis,
  fmtDateFull,
  fmtDuration,
  sliceRowsFromFirstJointOne,
  timeDiffSeconds,
  compareCoatingRowsChronological,
  DEFAULT_LIMITS,
  type TemperatureLimits,
} from './dashboardCalculations';
import { Notification as customNotification } from '../../../../../../components/ui/custom-notification';

function getAuthUserEmail(user: unknown): string {
  if (!user || typeof user !== 'object') return '';
  const u = user as Record<string, unknown>;
  if (typeof u.email === 'string' && u.email.trim()) return u.email.trim();
  const attrs = u.attributes as Record<string, unknown> | undefined;
  if (attrs && typeof attrs.email === 'string' && attrs.email.trim()) return attrs.email.trim();
  if (typeof u.username === 'string' && u.username.includes('@')) return u.username.trim();
  return '';
}

/** Cognito subject or equivalent — sent as optional `userId` with savePumpHouseLimitConfig. */
function getAuthUserSub(user: unknown): string {
  if (!user || typeof user !== 'object') return '';
  const u = user as Record<string, unknown>;
  if (typeof u.sub === 'string' && u.sub.trim()) return u.sub.trim();
  const attrs = u.attributes as Record<string, unknown> | undefined;
  if (attrs && typeof attrs.sub === 'string' && attrs.sub.trim()) return attrs.sub.trim();
  return '';
}

export interface CoatingRow {
  projectId: string | number;
  pipeId: string | number;
  user: string;
  coatingMaterial: string;
  coatingType: string;
  baseBatch: string;
  hardenerBatch: string;
  jointWidth: string;
  jointThickness: string;
  prime: string;
  flush: string;
  date: string;
  time: string;
  joint: string;
  waterTemp: string;
  baseTemp: string;
  hardenerTemp: string;
  ambientTemp: string;
  humidity: string;
  dewPoint: string;
  downstrokeStall: string;
  upstrokeStall: string;
  stallTestType: string;
  systemFlushed: string;
  systemPrimed: string;
  valveNumber: string;
  fittingNumber: string;
  isEvent: boolean;
  eventMessage?: string;
  s3Link?: string;
}

export interface ParsedCoatingFile {
  rows: CoatingRow[];
  fileName: string;
}

// ─── CSV / Excel parser ───────────────────────────────────────────────────────

const parseCsvText = (text: string, projectId: string | number = '', pipeId: string | number = ''): ParsedCoatingFile | null => {
  const rawLines = text.split(/\r?\n/).map(l => l.split(',').map(c => c.trim()));
  const metaKeys = rawLines[0] ?? [];
  const metaVals = rawLines[1] ?? [];
  const idx = (k: string) => metaKeys.findIndex(h => h.toLowerCase() === k.toLowerCase());

  const metaForRow = {
    projectId,
    pipeId,
    user:            metaVals[idx('User')] ?? '',
    coatingMaterial: metaVals[idx('Coating Material')] ?? '',
    coatingType:     metaVals[idx('Coating Type')] ?? '',
    baseBatch:       metaVals[idx('Base Batch')] ?? '',
    hardenerBatch:   metaVals[idx('Hardener Batch')] ?? '',
    jointWidth:      metaVals[idx('Joint Width')] ?? '',
    jointThickness:  metaVals[idx('Joint Thickness')] ?? '',
    prime:           metaVals[idx('Prime')] ?? '',
    flush:           metaVals[idx('Flush')] ?? '',
  };

  const rows: CoatingRow[] = [];
  for (let i = 4; i < rawLines.length; i++) {
    const cells = rawLines[i];
    if (!cells || cells.every(c => c === '')) continue;
    const isEvent =
      cells.length <= 4 && cells[2] != null && cells[2] !== '' &&
      isNaN(Number(cells[2])) &&
      cells[2].toLowerCase() !== 'pass' && cells[2].toLowerCase() !== 'fail';

    if (isEvent) {
      rows.push({
        ...metaForRow,
        date: cells[0] ?? '', time: cells[1] ?? '', joint: '',
        waterTemp: '', baseTemp: '', hardenerTemp: '', ambientTemp: '',
        humidity: '', dewPoint: '', downstrokeStall: '', upstrokeStall: '',
        stallTestType: '', systemFlushed: '', systemPrimed: '',
        valveNumber: '', fittingNumber: '',
        isEvent: true, eventMessage: cells[2],
      });
    } else {
      rows.push({
        ...metaForRow,
        date: cells[0] ?? '', time: cells[1] ?? '', joint: cells[2] ?? '',
        waterTemp: cells[3] ?? '', baseTemp: cells[4] ?? '', hardenerTemp: cells[5] ?? '',
        ambientTemp: cells[6] ?? '', humidity: cells[7] ?? '', dewPoint: cells[8] ?? '',
        downstrokeStall: cells[9] ?? '', upstrokeStall: cells[10] ?? '',
        stallTestType: cells[11] ?? '', systemFlushed: cells[12] ?? '',
        systemPrimed: cells[13] ?? '', valveNumber: cells[14] ?? '', fittingNumber: cells[15] ?? '',
        isEvent: false,
      });
    }
  }
  return { rows, fileName: '' };
};

export const parseExcelFile = async (file: File, projectId: string | number = '', pipeId: string | number = ''): Promise<ParsedCoatingFile | null> => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as string[][];
  const csvText = raw.map(r => r.map(c => String(c ?? '')).join(',')).join('\n');
  return parseCsvText(csvText, projectId, pipeId);
};

// ─── helpers ──────────────────────────────────────────────────────────────────

/**
 * Builds a map of filteredRows-index → cycle string.
 * Sorts rows chronologically first so cycles are always computed in time order,
 * regardless of the current display sort.
 * Cycle = time elapsed since the previous non-event row in chronological sequence.
 * Event rows and the first row display "—".
 */
const buildCycleMap = (rows: CoatingRow[]): Map<number, string> => {
  const indexed = rows.map((r, idx) => ({ r, idx }));
  indexed.sort((a, b) => compareCoatingRowsChronological(a.r, b.r));

  const map = new Map<number, string>();
  let prevTime: string | null = null;
  for (const { r, idx } of indexed) {
    if (r.isEvent) { map.set(idx, '—'); continue; }
    const delta = prevTime !== null ? timeDiffSeconds(prevTime, r.time) : -1;
    map.set(idx, delta > 0 ? fmtDuration(delta) : '—');
    prevTime = r.time;
  }
  return map;
};

const resultClass = (val: string) => {
  const v = val.toLowerCase();
  if (v === 'pass') return 'text-green-600 font-semibold';
  if (v === 'fail') return 'text-red-600 font-semibold';
  return '';
};

const flushCellExport = (row: CoatingRow) =>
  row.systemFlushed.toLowerCase() === 'flush ok' ? 'OK' : row.systemFlushed ? 'NO' : '—';

const primeCellExport = (row: CoatingRow) =>
  row.systemPrimed.toLowerCase() === 'prime ok' ? 'OK' : row.systemPrimed ? 'NO' : '—';

function sanitizeFilePart(s: string, maxLen: number): string {
  const t = String(s ?? '').replace(/[/\\?*[\]:]/g, '-').trim();
  return t.length > maxLen ? t.slice(0, maxLen) : t;
}

function downloadNameFromS3Link(url: string): string {
  try {
    const name = new URL(url).pathname.split('/').pop();
    return name ? decodeURIComponent(name) : 'pump-house-data.csv';
  } catch {
    return 'pump-house-data.csv';
  }
}

function downloadSessionSourceFile(s3Link: string): void {
  const trimmed = s3Link.trim();
  if (!trimmed) return;
  const anchor = document.createElement('a');
  anchor.href = trimmed;
  anchor.download = downloadNameFromS3Link(trimmed);
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

function exportPumpHouseDetailRowsToExcel(
  rows: CoatingRow[],
  fileBase: string,
): void {
  if (rows.length === 0) return;
  const cycleMapExport = buildCycleMap(rows);
  const headers = [
    'Time',
    'Joint',
    'Base Temp',
    'Hardener Temp',
    'DS Stall',
    'US Stall',
    'Flush',
    'Prime',
    'Cycle',
  ];
  const aoa: string[][] = [
    headers,
    ...rows.map((row, idx) => {
      const cycle = cycleMapExport.get(idx) ?? '—';
      if (row.isEvent) {
        return [row.time, row.eventMessage ?? '', '', '', '', '', '', '', cycle];
      }
      return [
        row.time,
        row.joint,
        row.baseTemp,
        row.hardenerTemp,
        row.downstrokeStall,
        row.upstrokeStall,
        flushCellExport(row),
        primeCellExport(row),
        cycle,
      ];
    }),
  ];
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  XLSX.utils.book_append_sheet(wb, ws, 'Pump House Detail');
  const safe = sanitizeFilePart(fileBase, 80) || 'PumpHouse_Detail';
  XLSX.writeFile(wb, `${safe}.xlsx`);
}

type SortDir = 'asc' | 'desc' | null;

const SortIcon: React.FC<{ dir: SortDir }> = ({ dir }) => (
  <span className="inline-flex flex-col ml-1 opacity-50 text-[10px] leading-none">
    <ChevronUp  className={`h-3 w-3 ${dir === 'asc'  ? 'opacity-100 text-blue-600' : ''}`} />
    <ChevronDown className={`h-3 w-3 ${dir === 'desc' ? 'opacity-100 text-blue-600' : ''}`} />
  </span>
);

// ─── component ────────────────────────────────────────────────────────────────

interface PumpHouseCoatingTableProps {
  selectedPipe?: any;
}

const PAGE_SIZES = [25, 50, 100, 200];

export const PumpHouseCoatingTable: React.FC<PumpHouseCoatingTableProps> = ({ selectedPipe }) => {
  const {
    coatingPumphouseData,
    coatingPumphouseLoading,
    getAllCoatingPumphouse,
    clearCoatingPumphouseData,
    savePumpHouseLimitConfig,
    pumpHouseLimitConfigFromApi,
  } = useCoatingPumphouse();

  const authUser = useSelector((state: { auth?: { user?: unknown } }) => state.auth?.user);
  const userEmailId = useMemo(() => getAuthUserEmail(authUser), [authUser]);
  const userIdForConfig = useMemo(() => getAuthUserSub(authUser), [authUser]);


  // Fetch from API whenever the selected pipe changes
  React.useEffect(() => {
    if (selectedPipe) {
      const projectId = selectedPipe?.projectId ?? selectedPipe?.project_id ?? '';
      const pipeId    = selectedPipe?.pipeId    ?? selectedPipe?.pipe_id    ?? selectedPipe?.id ?? '';
      getAllCoatingPumphouse({ projectId, pipeId });
    } else {
      clearCoatingPumphouseData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPipe?.pipeId, selectedPipe?.id, selectedPipe?.projectId, selectedPipe?.project_id]);

  // ── filter state ─────────────────────────────────────────────────────────
  const [resultFilter, setResultFilter] = useState<string[]>([]);
  /** When false, event rows are omitted from the table and from KPI / chart `rows` (not from Session timeline). */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for optional "Events" filter UI
  const [showEvents, setShowEvents] = useState(false);

  // ── sort state ────────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState<keyof CoatingRow | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  // ── pagination state ──────────────────────────────────────────────────────
  const [currentPage,  setCurrentPage]  = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // ── session drill-down state ──────────────────────────────────────────────
  const [selectedSession, setSelectedSession] = useState<{ date: string; user: string } | null>(null);
  /** Persisted temperature limits for session drilldown (survives closing the modal). */
  const [drillTemperatureLimits, setDrillTemperatureLimits] = useState<TemperatureLimits>(() => ({ ...DEFAULT_LIMITS }));
  const [drillTemperatureLimitsActive, setDrillTemperatureLimitsActive] = useState(false);

  const persistDrillTemperatureLimits = React.useCallback((payload: { limits: TemperatureLimits; active: boolean }) => {
    setDrillTemperatureLimits(payload.limits);
    setDrillTemperatureLimitsActive(payload.active);
    if (!selectedPipe) return;
    if (!userEmailId) {
      customNotification.warning(
        'Limits updated locally only',
        'Your account email was not found. Sign in again to save limits to the server.',
      );
      return;
    }
    const projectIdRaw = selectedPipe?.projectId ?? selectedPipe?.project_id;
    const projectId = projectIdRaw ?? '';
    const pipeId = selectedPipe?.pipeId ?? selectedPipe?.pipe_id ?? selectedPipe?.id ?? '';
    if (projectIdRaw == null || String(projectIdRaw).trim() === '') {
      customNotification.warning(
        'Limits updated locally only',
        'Project ID was not found on the selected pipe, so limits were not saved to the server.',
      );
      return;
    }
    savePumpHouseLimitConfig({
      userEmailId,
      userId: userIdForConfig || undefined,
      projectId,
      pipeId,
      limits: payload.limits,
      active: payload.active,
      maxRows: 5000,
      defaultPageSize: itemsPerPage,
    });
  }, [selectedPipe, userEmailId, userIdForConfig, itemsPerPage, savePumpHouseLimitConfig]);

  const activeDrillLimits = drillTemperatureLimitsActive ? drillTemperatureLimits : null;

  // Reset persisted drill limits when the pipe / project context changes
  React.useEffect(() => {
    setDrillTemperatureLimits({ ...DEFAULT_LIMITS });
    setDrillTemperatureLimitsActive(false);
  }, [selectedPipe?.pipeId, selectedPipe?.id]);

  // Apply server limits loaded with getAll → getPumpHouseLimitConfig?projectId=… (per project).
  React.useEffect(() => {
    if (!pumpHouseLimitConfigFromApi || !selectedPipe) return;
    const pid = String(selectedPipe?.projectId ?? selectedPipe?.project_id ?? '');
    const pPipe = String(selectedPipe?.pipeId ?? selectedPipe?.pipe_id ?? selectedPipe?.id ?? '');
    const c = pumpHouseLimitConfigFromApi;
    const cfgPid = c.projectId != null && c.projectId !== '' ? String(c.projectId) : '';
    const cfgPipe = c.pipeId != null && c.pipeId !== '' ? String(c.pipeId) : '';
    if (cfgPid && cfgPipe && (cfgPid !== pid || cfgPipe !== pPipe)) return;
    setDrillTemperatureLimits({ ...c.limits });
    setDrillTemperatureLimitsActive(!!c.active);
  }, [pumpHouseLimitConfigFromApi, selectedPipe]);

  // ── derived options from data ─────────────────────────────────────────────
  const allRows = (coatingPumphouseData as CoatingRow[]) ?? [];

  /** Rows scoped to the selected session (date + user), or all rows when none is selected. */
  const sessionRows = useMemo(() => {
    if (!selectedSession) return allRows;
    return allRows.filter(r =>
      r.date === selectedSession.date &&
      // Event rows have no user — include them if they fall on the same date
      (r.isEvent ? true : r.user === selectedSession.user)
    );
  }, [allRows, selectedSession]);

  /** Drilldown scope: same session rows, trimmed to start at the first chronological joint #1 (FPY / table). */
  const sessionRowsDetail = useMemo(() => {
    if (!selectedSession) return sessionRows;
    return sliceRowsFromFirstJointOne(sessionRows);
  }, [selectedSession, sessionRows]);

  const detailSessionKpis = useMemo(() => {
    if (!selectedSession) return null;
    return computeSessionKpis(sessionRowsDetail);
  }, [selectedSession, sessionRowsDetail]);

  const sessionSummaries = useMemo(() => buildSessionSummaries(allRows), [allRows]);

  const sessionExcursionTotals = useMemo(() => {
    if (!drillTemperatureLimitsActive) return undefined;
    return buildSessionExcursionTotals(allRows, drillTemperatureLimits);
  }, [allRows, drillTemperatureLimits, drillTemperatureLimitsActive]);

  const resultOptions    = [
    { value: 'pass',  label: '✓ Pass'  },
    { value: 'fail',  label: '✗ Fail'  },
    { value: 'event', label: '⚡ Event' },
  ];

  const hasActiveFilters = resultFilter.length > 0;

  const handleClearFilters = () => {
    setResultFilter([]);
    setCurrentPage(1);
  };


  // Reset to page 1 when filters change
  React.useEffect(() => { setCurrentPage(1); }, [resultFilter, showEvents]);

  const toggleSort = (key: keyof CoatingRow) => {
    if (sortKey === key) {
      if (sortDir === 'asc') { setSortDir('desc'); }
      else if (sortDir === 'desc') { setSortKey(null); setSortDir(null); }
      else { setSortDir('asc'); }
    } else {
      setSortKey(key); setSortDir('asc');
    }
  };

  const getSortIcon = (key: keyof CoatingRow) =>
    sortKey === key ? <SortIcon dir={sortDir} /> : <SortIcon dir={null} />;

  // ── filtering ─────────────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    // Inside the drilldown modal: session trimmed from first joint #1; overview uses all rows
    let rows = selectedSession ? [...sessionRowsDetail] : [...allRows];

    if (!showEvents) rows = rows.filter(r => !r.isEvent);

    if (resultFilter.length > 0) {
      rows = rows.filter(r => {
        if (resultFilter.includes('event') && r.isEvent) return true;
        if (r.isEvent) return false;
        const ds = r.downstrokeStall.toLowerCase();
        const us = r.upstrokeStall.toLowerCase();
        if (resultFilter.includes('pass') && ds === 'pass' && us === 'pass') return true;
        if (resultFilter.includes('fail') && (ds === 'fail' || us === 'fail')) return true;
        return false;
      });
    }

    if (sortKey && sortDir) {
      rows = [...rows].sort((a, b) => {
        const cmp =
          sortKey === 'time'
            ? compareCoatingRowsChronological(a, b)
            : (() => {
                const av = String(a[sortKey] ?? '');
                const bv = String(b[sortKey] ?? '');
                return isNaN(Number(av)) || isNaN(Number(bv))
                  ? av.localeCompare(bv)
                  : Number(av) - Number(bv);
              })();
        return sortDir === 'asc' ? cmp : -cmp;
      });
    } else {
      rows = [...rows].sort(compareCoatingRowsChronological);
    }

    return rows;
  }, [allRows, sessionRowsDetail, selectedSession, resultFilter, showEvents, sortKey, sortDir]);

  /**
   * Drilldown timeline + KPI scope: every joint from the first joint #1 onward (see
   * `sessionRowsDetail`), merged with session events so E-stops still plot. Pass/Fail
   * table filters do not shrink yield or the timeline — only the table/chart `rows` prop does.
   */
  const drillTimelineRows = useMemo(() => {
    if (!selectedSession) return filteredRows;
    const jointRows     = sessionRowsDetail.filter(r => !r.isEvent);
    const sessionEvents = sessionRowsDetail.filter(r => r.isEvent);
    const rowKey = (r: CoatingRow) =>
      `${r.date}\t${r.time}\t${r.isEvent ? 1 : 0}\t${r.isEvent ? (r.eventMessage ?? '') : r.joint}`;
    const byKey = new Map<string, CoatingRow>();
    for (const r of jointRows) byKey.set(rowKey(r), r);
    for (const r of sessionEvents) byKey.set(rowKey(r), r);
    return Array.from(byKey.values()).sort(compareCoatingRowsChronological);
  }, [selectedSession, sessionRowsDetail, filteredRows]);

  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const displayRows = filteredRows.slice((safeCurrentPage - 1) * itemsPerPage, safeCurrentPage * itemsPerPage);

  // Cycle time per displayed row — offset by page start so index into filteredRows is correct.
  const cycleMap = buildCycleMap(filteredRows);
  const pageOffset = (safeCurrentPage - 1) * itemsPerPage;

  // ── early returns (after all hooks) ──────────────────────────────────────

  if (coatingPumphouseLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-10 h-10 border-4 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
        <p className="text-gray-500 text-sm">Loading coating data…</p>
      </div>
    );
  }

  if (allRows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
          <Upload className="h-8 w-8 text-purple-500" />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-[#427290]">Pump House Coating Data</h3>
          <p className="text-gray-500 text-sm mt-1 max-w-sm">
            Use the "Pump House Coating Upload" button above to load a CSV or Excel file.
          </p>
        </div>
      </div>
    );
  }

  // ── table view ────────────────────────────────────────────────────────────

  const selectedSessionSummary = selectedSession
    ? sessionSummaries.find(s => s.date === selectedSession.date && s.user === selectedSession.user)
    : undefined;

  return (
    <>
      {/* ── overview (always visible) ─────────────────────────────────────── */}
      <div className="w-full space-y-4">
        <SprayDashboardSection
          rows={allRows}
          temperatureLimits={drillTemperatureLimits}
          limitsActive={drillTemperatureLimitsActive}
          headerRight={
            selectedPipe ? (
              <PumpHouseLimitsToolbar
                variant="inline"
                limits={drillTemperatureLimits}
                limitsActive={drillTemperatureLimitsActive}
                onPersist={persistDrillTemperatureLimits}
              />
            ) : null
          }
        />
        <SessionsAggregateTable
          sessions={sessionSummaries}
          limitsActive={drillTemperatureLimitsActive}
          excursionBySessionKey={sessionExcursionTotals}
          onSelect={(date, user) => { setSelectedSession({ date, user }); setCurrentPage(1); }}
        />
      </div>

      {/* ── full-screen drilldown modal ───────────────────────────────────── */}
      {selectedSession && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: '#FFFFFF', overflowY: 'auto', display: 'flex', flexDirection: 'column',
        }}>
          {/* sticky modal header */}
          <div style={{
            position: 'sticky', top: 0, zIndex: 10, background: '#FFFFFF',
            borderBottom: '1px solid #E2E5EA', padding: '10px 20px',
            display: 'flex',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              {/* left: arrow + divider (fixed width so right column aligns) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, paddingTop: 2 }}>
                <button
                  onClick={() => setSelectedSession(null)}
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '5px 8px', cursor: 'pointer',
                    border: '1px solid #E2E5EA', borderRadius: 8,
                    background: '#FFFFFF', color: '#00273F', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#00273F'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.color = '#00273F'; }}
                >
                  <ArrowLeft style={{ width: 15, height: 15 }} />
                </button>
                <span style={{ width: 1, height: 36, background: '#E2E5EA', flexShrink: 0 }} />
              </div>

              {/* right: date heading (row 1) + pills (row 2) — both share the same left edge */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                {selectedSessionSummary && (
                  <span style={{ fontSize: 17, fontWeight: 600, color: '#00273F', lineHeight: 1.2 }}>
                    {fmtDateFull(selectedSessionSummary.date)}
                  </span>
                )}
                {selectedSessionSummary && (() => {
                  const colorPalette = [
                    'bg-blue-100', 'bg-purple-100', 'bg-orange-100',
                    'bg-pink-100', 'bg-teal-100',   'bg-amber-100', 'bg-slate-100',
                  ];
                  const pills = [
                    { label: 'Operator',       value: selectedSessionSummary.user },
                    // FUTURE: { label: 'Coating', value: selectedSessionSummary.coatingType },
                    { label: 'Base Batch',     value: selectedSessionSummary.baseBatch },
                    { label: 'Hardener Batch', value: selectedSessionSummary.hardenerBatch },
                    { label: 'Joints',         value: String(detailSessionKpis?.uniqueJoints ?? selectedSessionSummary.joints) },
                    { label: 'First pass yield', value: `${(detailSessionKpis?.fpYield ?? selectedSessionSummary.fpYield).toFixed(1)}%` },
                    { label: 'Total yield',      value: `${(detailSessionKpis?.totalYield ?? selectedSessionSummary.totalYield).toFixed(1)}%` },
                    { label: 'Records',        value: String(filteredRows.length) },
                  ];
                  return (
                    <div className="flex flex-wrap items-center gap-2">
                      {pills.map((p, i) => (
                        <span
                          key={p.label}
                          className={`${colorPalette[i % colorPalette.length]} px-2 py-1 rounded text-xs font-medium text-gray-600`}
                        >
                          {p.label}: {p.value}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* modal body */}
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">

            {/* session-specific visualizations */}
            {selectedSessionSummary && (
              <SessionDrilldownSection
                rows={filteredRows}
                timelineRows={drillTimelineRows}
                session={selectedSessionSummary}
                drillTemperatureLimits={drillTemperatureLimits}
                drillTemperatureLimitsActive={drillTemperatureLimitsActive}
              />
            )}

      {/* ── filter panel (Pass / Fail / Event only) ────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="font-medium">Filters</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={handleClearFilters} className="text-gray-500 hover:text-gray-700">
                <X className="h-4 w-4 mr-1" />Clear filter
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 w-full">
          <div className="w-[220px] shrink-0">
            <MultiSelect
              options={resultOptions}
              selected={resultFilter}
              onChange={setResultFilter}
              placeholder="Pass / Fail / Event"
            />
          </div>
          <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
            {selectedSessionSummary?.s3Link ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => downloadSessionSourceFile(selectedSessionSummary.s3Link!)}
                title="Download original upload file from storage"
                className="shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4 shrink-0" />
                  Download Raw Data
                </div>
              </Button>
            ) : null}
            <Button
              size="sm"
              disabled={filteredRows.length === 0}
              onClick={() => {
                if (!selectedSession || filteredRows.length === 0) return;
                const datePart = sanitizeFilePart(selectedSession.date, 24);
                const userPart = sanitizeFilePart(selectedSession.user, 32);
                const base = `PumpHouse_Detail_${datePart}_${userPart}`;
                exportPumpHouseDetailRowsToExcel(filteredRows, base);
              }}
              title="Download all rows matching current filters and sort as Excel"
              className="bg-[#427290] hover:bg-[#427290]/90 text-white shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2">
                <Download className="h-4 w-4 shrink-0" />
                Export Table
              </div>
            </Button>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
              Result: {resultFilter.join(', ')}
              <button type="button" onClick={() => setResultFilter([])} aria-label="Clear result filter"><X className="h-3 w-3" /></button>
            </span>
          </div>
        )}

      </div>

      {/* ── table ────────────────────────────────────────────────────────── */}
      <div className="flex min-h-[420px] flex-1 flex-col overflow-hidden rounded-lg border border-[#E2E5EA] bg-white">
        <div className="min-h-0 flex-1 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <Table className="min-w-full">
              <TableHeader>
                <TableRow className="border-b h-[56px]">
                  {/* col 1: Time */}
                  <TableHead
                    className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                    onClick={() => toggleSort('time')}
                  >
                    <div className="flex items-center gap-1">Time{getSortIcon('time')}</div>
                  </TableHead>
                  {/* col 2: Joint, then remaining data cols, then meta cols */}
                  {([
                    ['joint',           'Joint'],
                    // FUTURE: detailed temp / env columns
                    // ['waterTemp',       'Water Temp'],
                    ['baseTemp',        'Base Temp'],
                    ['hardenerTemp',    'Hardener Temp'],
                    // ['ambientTemp',     'Ambient Temp'],
                    // ['humidity',        'Humidity'],
                    // ['dewPoint',        'Dew Point'],
                    ['downstrokeStall', 'DS Stall'],
                    ['upstrokeStall',   'US Stall'],
                    // ['stallTestType',   'Stall Type'],
                    ['systemFlushed',   'Flush'],
                    ['systemPrimed',    'Prime'],
                  ] as [keyof CoatingRow, string][]).map(([key, label]) => (
                    <TableHead
                      key={key}
                      className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                      onClick={() => toggleSort(key)}
                    >
                      <div className="flex items-center gap-1">{label}{getSortIcon(key)}</div>
                    </TableHead>
                  ))}
                  {/* static Cycle column */}
                  <TableHead className="sticky top-0 z-20 bg-gray-50 text-xs font-medium text-gray-600 whitespace-nowrap">
                    Cycle
                  </TableHead>
                </TableRow>
              </TableHeader>

                <TableBody>
                  {displayRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                        No records match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayRows.map((row, i) => {
                      if (row.isEvent) {
                        return (
                          <TableRow key={i} className="bg-yellow-50 hover:bg-yellow-100 border-b border-gray-100">
                            {/* col 1: Time */}
                            <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                              {row.time}
                            </TableCell>
                            {/* col 2: event message spans all remaining cols including Cycle */}
                            <TableCell colSpan={8} className="text-xs text-yellow-700 font-medium italic">
                              <span className="inline-flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                {row.eventMessage}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      }

                      const isStallFail =
                        row.downstrokeStall.toLowerCase() === 'fail' ||
                        row.upstrokeStall.toLowerCase() === 'fail';
                      const isSensorDrop =
                        row.waterTemp === '0' || row.baseTemp === '0' || row.hardenerTemp === '0';
                      const isFlushWarn =
                        row.systemFlushed !== '' && row.systemFlushed.toLowerCase() !== 'flush ok';
                      const isPrimeWarn =
                        row.systemPrimed !== '' && row.systemPrimed.toLowerCase() !== 'prime ok';
                      const isWarnRow = !isStallFail && (isSensorDrop || isFlushWarn || isPrimeWarn);


                      // excursion helpers (only when limits are active)
                      const L = activeDrillLimits;
                      const isExc = (val: string, min: number, max: number): boolean => {
                        if (!L) return false;
                        const n = parseFloat(val);
                        if (!n || isNaN(n)) return false;
                        return n < min || n > max;
                      };

                      const wExc = isExc(row.waterTemp,    L?.waterMin ?? 0,    L?.waterMax ?? 9999);
                      const bExc = isExc(row.baseTemp,     L?.baseMin ?? 0,     L?.baseMax ?? 9999);
                      const hExc = isExc(row.hardenerTemp, L?.hardenerMin ?? 0, L?.hardenerMax ?? 9999);
                      const isExcRow = wExc || bExc || hExc;

                      return (
                        <TableRow
                          key={i}
                          className={`border-b border-gray-100 ${
                            isStallFail ? 'bg-red-50 hover:bg-red-100'
                            : isWarnRow || isExcRow ? 'bg-amber-50 hover:bg-amber-100'
                            : 'hover:bg-gray-50'
                          }`}
                        >
                          {/* col 1: Time */}
                          <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                            {row.time}
                          </TableCell>
                          {/* col 2: Joint */}
                          <TableCell className="text-xs font-medium text-gray-800">{row.joint}</TableCell>
                          {/* temp cols — show △ marker on excursion cells */}
                          {/*
                          FUTURE: Water (header commented above)
                          <TableCell className={`text-xs ${wExc ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
                            {row.waterTemp}{wExc ? ' △' : ''}
                          </TableCell>
                          */}
                          <TableCell className={`text-xs ${bExc ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
                            {row.baseTemp}{bExc ? ' △' : ''}
                          </TableCell>
                          <TableCell className={`text-xs ${hExc ? 'text-amber-700 font-medium' : 'text-gray-600'}`}>
                            {row.hardenerTemp}{hExc ? ' △' : ''}
                          </TableCell>
                          {/*
                          FUTURE: Ambient
                          <TableCell className="text-xs text-gray-600">{row.ambientTemp}</TableCell>
                          */}
                          {/*
                          <TableCell className="text-xs text-gray-600">{row.humidity}</TableCell>
                          */}
                          {/*
                          <TableCell className="text-xs text-gray-600">{row.dewPoint}</TableCell>
                          */}
                          <TableCell className={`text-xs ${resultClass(row.downstrokeStall)}`}>{row.downstrokeStall}</TableCell>
                          <TableCell className={`text-xs ${resultClass(row.upstrokeStall)}`}>{row.upstrokeStall}</TableCell>
                          {/*
                          <TableCell className="text-xs text-gray-600">{row.stallTestType}</TableCell>
                          */}
                          <TableCell className={`text-xs ${isFlushWarn ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{row.systemFlushed.toLowerCase() === 'flush ok' ? 'OK' : row.systemFlushed ? 'NO' : '—'}</TableCell>
                          <TableCell className={`text-xs ${isPrimeWarn ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{row.systemPrimed.toLowerCase() === 'prime ok' ? 'OK' : row.systemPrimed ? 'NO' : '—'}</TableCell>
                          <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                            {cycleMap.get(pageOffset + i) ?? '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
        </div>
        <div className="sticky bottom-0 z-10 flex-shrink-0 border-t border-[#E2E5EA] bg-white/95 px-4 py-3 backdrop-blur">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-6 text-[12px] text-text-grey">
              <div className="flex items-center gap-2">
                <span className="font-medium">Showing:</span>
                <span className="font-semibold text-blueprint-blue">
                  {totalItems > 0 ? `${(safeCurrentPage - 1) * itemsPerPage + 1}-${Math.min(safeCurrentPage * itemsPerPage, totalItems)}` : '0'} of {totalItems}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span>Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="rounded-lg border border-[#E2E5EA] bg-white px-2 py-1 text-[12px] text-text-grey"
                  title="Items per page"
                >
                  {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1} className="hidden md:inline-flex">First</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safeCurrentPage === 1}>Previous</Button>
                <span className="px-2 text-[12px] text-text-grey">Page {safeCurrentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safeCurrentPage === totalPages}>Next</Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage === totalPages} className="hidden md:inline-flex">Last</Button>
              </div>
            )}
          </div>
        </div>
      </div>

          </div>{/* end modal body */}
        </div>
      )}{/* end modal */}
    </>
  );
};
